import { Component, Vue, Prop, Watch, Ref } from 'vue-property-decorator';
import { Camera } from '@/models/camera';
import { Project } from '@/models/project';
import { Photo } from '@/models/photo';
import { TimelapseConfiguration } from '@/models/timelapseconfiguration';
import { State, Action } from 'vuex-class';
import VueSlider from 'vue-slider-component';
import VueTimepicker from 'vue2-timepicker/src/vue-timepicker.vue';
import * as d3 from 'd3';
import * as Plotly from 'plotly.js';
import { ModeBarDefaultButtons } from 'plotly.js';
import { Storage } from 'aws-amplify';
// @ts-ignore no d.ts
import VirtualCollection from 'vue-virtual-collection';
import S3Image from '../s3-image/S3Image.vue';
import { photoService } from '@/services';
import { DeletePhotoRequest } from '@/services/photos/photos.interfaces';
import { NotificationMessage, Socket } from '@/store/types';
import { Logger } from 'aws-amplify';
const logger = new Logger('TimelapsePhoto');

@Component({
  components: {
    VueSlider,
    VueTimepicker,
    S3Image,
  },
})
export default class TimelapsePhoto extends Vue {
  svg: any;
  @Ref('scroller') readonly scroller?: VirtualCollection | undefined;
  @Ref('photos') readonly photos!: HTMLDivElement;

  @Prop({ required: true, type: Object }) readonly camera!: Camera;
  @Prop({ required: true, type: Object }) readonly project!: Project;
  @Prop({ required: true, type: Object }) readonly value!: TimelapseConfiguration;
  @Prop({ required: true }) readonly dataImagesKey!: { data: Photo }[];
  @Prop() readonly total?: number;
  @Prop({ required: true, type: Boolean }) public readonly timelapseRending!: boolean;
  @Prop({ required: true }) readonly isPremium!: boolean;
  @Prop({ type: Boolean, default: true }) readonly withinInterval!: boolean;

  @State('locale', { namespace: 'profile' }) locale!: string;
  @State('socket') socketMessage!: Socket;
  @State('photosByCameraId', { namespace: 'photo' }) photosByCameraId!: { [cameraId: string]: Photo[] };
  @Action('connectSocket') private connectSocket!: () => Promise<void>;
  @Action('disconnectSocket') private disconnectSocket!: () => Promise<void>;
  @State('isLoading', { namespace: 'photo' }) readonly loadingPhoto!: boolean;
  @State('loadingTimelapse', { namespace: 'project' }) readonly loadingTimelapse!: boolean;
  @Action('deletePhotos', { namespace: 'photo' }) deletePhotos!: (
    deletePhotoRequest: DeletePhotoRequest
  ) => Promise<void>;

  get cameraPhotos(): Photo[] {
    return this.photosByCameraId[this.camera.id] || [];
  }

  // Adapter `filteredImagesKey` pour utiliser `cameraPhotos`
  get filteredImagesKey() {
    return this.localImages.filter((photoWrapper: { data: Photo }) => {
      const photo = photoWrapper.data;
      const goutteValue = photo.goutte !== undefined ? photo.goutte : 0;
      return goutteValue >= this.model.goutte[0] && goutteValue <= this.model.goutte[1];
    });
  }

  get visiblePhotos(): Photo[] {
    return this.filteredImagesKey.map((w) => w.data);
  }

  get enabledCount() {
    const excluded = new Set(this.model?.photoExclusion ?? []);
    return this.localImages.reduce((acc, w) => {
      const p = w.data;
      const goutte = p.goutte ?? 0;
      const ok = goutte >= this.model.goutte[0] && goutte <= this.model.goutte[1] && !excluded.has(p.id);
      return acc + (ok ? 1 : 0);
    }, 0);
  }

  private get model(): TimelapseConfiguration {
    return this.localModel;
  }

  private set model(val: TimelapseConfiguration) {
    this.localModel = val;
  }
  /*
  private model: TimelapseConfiguration = { ...this.value };
  private get computedModel(): TimelapseConfiguration {
    return Object.assign({}, this.model);
  }
  @Watch('computedModel', { deep: true })
  public onModelChange(val: TimelapseConfiguration, oldVal: TimelapseConfiguration): void {
    const compare = JSON.stringify(val).localeCompare(JSON.stringify(oldVal));
    if (compare !== 0) {
      this.$emit('input', val);
    }
  }

  @Watch('value', { deep: true })
  public onValueChange(val: TimelapseConfiguration): void {
    this.model = { ...val };
  }
  */

  @Watch('value', { immediate: true, deep: true })
  public onValueChange(newVal: TimelapseConfiguration): void {
    this.localModel = { ...newVal };
  }

  public get dateFormat(): string {
    return this.locale === 'en' ? 'MM / DD / YY - HH:mm' : 'DD / MM / YY - HH:mm';
  }

  public get duree(): string {
    if (!this.withinInterval) return '';

    // nombre d’images réellement actives (option 2)
    const frames = this.enabledCount;

    // sécurité au cas où fps vaudrait 0
    const fps = Math.max(1, this.model.fps || 0);

    const totalSeconds = Math.floor(frames / fps);

    const d = new Date(0);
    d.setSeconds(totalSeconds);

    // format HH:MM:SS
    return d.toISOString().substr(11, 8);
  }

  private scrollerWidth = 0;
  private scrollerHeight = 0;
  private scrollerColumns = 0;
  private scrollerImageWidth = 0;

  // Bornes/valeurs par défaut Desktop
  private readonly DESKTOP_MIN = 4;
  private readonly DESKTOP_MAX = 20;
  private readonly DESKTOP_DEFAULT = 8;

  // Bornes/valeurs Mobile
  private readonly MOBILE_MIN = 2;
  private readonly MOBILE_MAX = 4;
  private readonly MOBILE_DEFAULT = 3;

  private photosPerRow = this.DESKTOP_DEFAULT;
  private minPhotosPerRow = this.DESKTOP_MIN;
  private maxPhotosPerRow = this.DESKTOP_MAX;
  private lastSelectedPhoto: Photo | undefined = undefined;
  private dataReady = false;
  private selectedPhoto: Photo[] = [];
  private zipGenerating = false;
  private zipLink: string[] | null = null;
  private rerender = 0;
  private sortAscending = true;
  private sortAscendingExposure = true;
  private sortAscendingSharpness = true;
  private showChart = false;
  public mousePosition = { x: 0, y: 0 };
  public hoverInfoPosition = { x: 0, y: 0 };
  public graphActivated = false;
  private localImages: { data: Photo }[] = [];
  private currentSortKey: 'date' | 'exposure' | 'iso' | 'sharpness' | 'movement' = 'date';
  private currentSortAsc = true;
  private localModel: TimelapseConfiguration = { ...this.value };
  private hoverOffsetX = 12; // offset horizontal dynamique
  private hoverOffsetY = -230; // offset vertical dynamique (sera recalculé)

  private activeImageUrl: string | null = null;
  private nextImageUrl: string | null = null;
  private isCrossfading = false;
  private imgLoadSeq = 0; // évite les courses si on clique très vite
  private readonly FADE_MS = 260; // durée en ms (garde en phase avec le CSS)
  private overlayPhotos: Photo[] = [];

  private showAllOverlays = false;

  public set showModalZip(set: boolean) {
    if (set && this.$route.query.modal === undefined) {
      this.$router.push({ path: this.$route.path, query: { ...this.$route.query, modal: 'zip' } });
    } else if (this.$route.query.modal === 'zip') {
      this.disconnectSocket();
      this.$router.push({ path: this.$route.path, query: { ...this.$route.query, modal: undefined } });
    }
  }

  public get showModalZip(): boolean {
    return this.$route.query.modal === 'zip';
  }

  public get zoomedPhoto(): Photo | null {
    const idx = this.zoomedPhotoIndex;
    const arr = this.overlayPhotos.length ? this.overlayPhotos : this.visiblePhotos;
    return idx !== undefined && arr[idx] ? arr[idx] : null;
  }

  get overlayActiveList(): Photo[] {
    return this.overlayPhotos.length ? this.overlayPhotos : this.visiblePhotos;
  }
  get overlayMaxIndex(): number {
    return this.overlayActiveList.length - 1;
  }

  public get zoomedPhotoIndex(): number | undefined {
    return this.$route.query.zoom ? parseInt(this.$route.query.zoom as string, 10) : undefined;
  }

  public set zoomedPhotoIndex(photoIndex: number | undefined) {
    const q = { ...this.$route.query };
    if (photoIndex === undefined) {
      delete q.zoom;
    } else {
      q.zoom = String(photoIndex);
    }
    this.$router.replace({ path: this.$route.path, query: q });
  }

  get selectablePhotos(): Photo[] {
    const excluded = new Set(this.model?.photoExclusion ?? []);
    // On part des visibles, mais on enlève celles qui sont "removed"
    return this.visiblePhotos.filter((p) => !excluded.has(p.id));
  }

  get allSelectableSelected(): boolean {
    const list = this.selectablePhotos; // déjà dispo chez toi
    return list.length > 0 && list.every((p) => this.selectedPhoto.some((sp) => sp.id === p.id));
  }

  get outputWidthOptions() {
    return [
      { value: 4624, text: 'Natif (4624)' },
      { value: 4000, text: '4K (4000)' },
      { value: 3840, text: '4K (3840)' },
      { value: 3000, text: 'High (3000)' },
      { value: 2560, text: '2K (2560)' },
      { value: 1920, text: 'Full HD (1920)' },
    ];
  }

  private handleWheelZoom = (event: WheelEvent) => {
    // Vérifie si Ctrl (ou Cmd sur Mac) est pressé
    if (event.ctrlKey || event.metaKey) {
      // Empêche le zoom du navigateur
      event.preventDefault();

      // deltaY > 0 = scroll vers le bas (zoom out)
      // deltaY < 0 = scroll vers le haut (zoom in)
      if (event.deltaY < 0) {
        // Scroll vers le haut = zoom in = decrease photos per row
        this.decreasePhotosPerRow();
      } else if (event.deltaY > 0) {
        // Scroll vers le bas = zoom out = increase photos per row
        this.increasePhotosPerRow();
      }
    }
  };

  public changeSortMethod(event: Event) {
    const v = (event.target as HTMLSelectElement).value;
    switch (v) {
      case 'sortByDateAsc':
        this.currentSortKey = 'date';
        this.currentSortAsc = true;
        break;
      case 'sortByDateDesc':
        this.currentSortKey = 'date';
        this.currentSortAsc = false;
        break;
      case 'sortByExposureAsc':
        this.currentSortKey = 'exposure';
        this.currentSortAsc = true;
        break;
      case 'sortByExposureDesc':
        this.currentSortKey = 'exposure';
        this.currentSortAsc = false;
        break;
      case 'sortByISOAsc':
        this.currentSortKey = 'iso';
        this.currentSortAsc = true;
        break;
      case 'sortByISODesc':
        this.currentSortKey = 'iso';
        this.currentSortAsc = false;
        break;
      case 'sortBySharpnessAsc':
        this.currentSortKey = 'sharpness';
        this.currentSortAsc = true;
        break;
      case 'sortBySharpnessDesc':
        this.currentSortKey = 'sharpness';
        this.currentSortAsc = false;
        break;
      case 'sortByMovementAsc':
        this.currentSortKey = 'movement';
        this.currentSortAsc = true;
        break;
      case 'sortByMovementDesc':
        this.currentSortKey = 'movement';
        this.currentSortAsc = false;
        break;
      default:
        // si jamais une nouvelle option arrive
        this.currentSortKey = 'date';
        this.currentSortAsc = true;
        break;
    }
    this.applyCurrentSort();
    if (this.showChart) this.createChartDebounced();
  }

  get removedVisibleIds(): string[] {
    const excluded = new Set(this.model?.photoExclusion ?? []);
    return this.visiblePhotos.filter((p) => excluded.has(p.id)).map((p) => p.id);
  }

  /** True s'il existe au moins une photo visible en remove */
  get hasVisibleRemoved(): boolean {
    return this.removedVisibleIds.length > 0;
  }

  /** Réactive toutes les photos visibles marquées remove (les retire de photoExclusion) */
  public restoreVisibleRemoved(): void {
    const toRestore = this.removedVisibleIds;
    if (!toRestore.length) return;

    const setToRestore = new Set(toRestore);
    const current = this.model?.photoExclusion ?? [];

    // retire uniquement celles visibles & en remove
    this.model.photoExclusion = current.filter((id) => !setToRestore.has(id));

    // (optionnel) tu peux vider la sélection ou la mettre à jour si tu veux :
    // this.selectedPhoto = this.visiblePhotos.slice();

    // force un léger rafraîchissement d'affichage si nécessaire
    this.rerender++;
    this.$emit('input', this.model);
    this.$nextTick(() => {
      const tt = this.$refs.restoreTooltip as any;
      if (tt) tt.$emit('close');
    });
  }

  private applyCurrentSort(): void {
    const k = this.currentSortKey;
    const asc = this.currentSortAsc ? 1 : -1;

    this.localImages.sort((a, b) => {
      let A: number;
      let B: number;

      switch (k) {
        case 'date':
          A = new Date(a.data.createdAt).getTime();
          B = new Date(b.data.createdAt).getTime();
          break;

        case 'exposure':
          A = a.data.exposure ?? 0;
          B = b.data.exposure ?? 0;
          break;

        case 'iso':
          A = a.data.gainControl ?? 0;
          B = b.data.gainControl ?? 0;
          break;

        case 'sharpness':
          A = a.data.sharpness ?? 0;
          B = b.data.sharpness ?? 0;
          break;

        case 'movement':
          A = a.data.movement ?? 0;
          B = b.data.movement ?? 0;
          break;

        default:
          A = 0;
          B = 0;
      }

      return asc * (A - B);
    });

    this.rerender++;
  }

  public sortByDate(ascending: boolean): void {
    this.localImages.sort((a, b) => {
      const dateA = new Date(a.data.createdAt).getTime();
      const dateB = new Date(b.data.createdAt).getTime();
      return ascending ? dateA - dateB : dateB - dateA;
    });
    this.rerender++;
  }

  public sortByExposure(ascending: boolean): void {
    this.localImages.sort((a, b) => {
      const exposureA = a.data.exposure !== undefined ? a.data.exposure : 0;
      const exposureB = b.data.exposure !== undefined ? b.data.exposure : 0;
      return ascending ? exposureA - exposureB : exposureB - exposureA;
    });
    this.rerender++;
  }

  public sortByISO(ascending: boolean): void {
    this.localImages.sort((a, b) => {
      const isoA = a.data.gainControl !== undefined ? a.data.gainControl : 0;
      const isoB = b.data.gainControl !== undefined ? b.data.gainControl : 0;
      return ascending ? isoA - isoB : isoB - isoA;
    });
    this.rerender++;
  }

  public sortBySharpness(ascending: boolean): void {
    this.localImages.sort((a, b) => {
      const sharpnessA = a.data.sharpness !== undefined ? a.data.sharpness : 0;
      const sharpnessB = b.data.sharpness !== undefined ? b.data.sharpness : 0;
      return ascending ? sharpnessA - sharpnessB : sharpnessB - sharpnessA;
    });
    this.rerender++;
  }

  public sortByMovement(ascending: boolean): void {
    this.localImages.sort((a, b) => {
      const movementA = a.data.movement !== undefined ? a.data.movement : 0;
      const movementB = b.data.movement !== undefined ? b.data.movement : 0;
      return ascending ? movementA - movementB : movementB - movementA;
    });
    this.rerender++;
  }

  public toggleChart(): void {
    this.showChart = !this.showChart;
    this.graphActivated = this.showChart;
    if (this.showChart) {
      this.applyCurrentSort();
      this.createChartDebounced();
    } else {
      const plotDiv: any = document.getElementById('chart');
      if (plotDiv) Plotly.purge(plotDiv);
      const hoverInfoDiv = document.getElementById('hoverinfo');
      if (hoverInfoDiv) hoverInfoDiv.innerHTML = '';
    }
  }

  public openScenarioModal() {
    this.$emit('open-scenario-modal', this.model);
  }

  private createChartDebounced = (() => {
    let raf = 0;
    return () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => this.createChart());
    };
  })();

  public async createChart() {
    // --- 1) S'assurer d'avoir un cache local pour les URLs S3
    type ImgCache = Map<string, string>;
    if (!(this as any).__imgUrlCache) {
      (this as any).__imgUrlCache = new Map() as ImgCache;
    }
    const imgCache: ImgCache = (this as any).__imgUrlCache;

    // --- 2) On repart de l’ordre couramment appliqué sur localImages
    const ordered = [...this.localImages];

    // --- 3) Construction des lignes (avec cache S3)
    const rows = (
      await Promise.all(
        ordered.map(async (wrap) => {
          const p = wrap.data;
          let imageUrl = imgCache.get(p.smallKey);
          if (!imageUrl) {
            const urlResult = await Storage.get(p.smallKey);
            imageUrl = typeof urlResult === 'string' ? urlResult : String(urlResult);
            imgCache.set(p.smallKey, imageUrl);
          }
          return {
            date: new Date(p.createdAt),
            exposure: p.exposure,
            iso: p.gainControl,
            sharpness: p.sharpness,
            person: p.person,
            movement: p.movement,
            imageUrl,
          };
        })
      )
    )
      // --- 4) Filtrage minimal + mouvement seulement si détection IA active
      .filter(
        (d) =>
          d.date &&
          d.exposure !== undefined &&
          d.iso !== undefined &&
          d.sharpness !== undefined &&
          d.imageUrl &&
          (this.camera.detectionia ? d.movement !== undefined : true)
      );

    // Si rien à tracer, on nettoie et on sort proprement
    const plotDiv: any = document.getElementById('chart');
    const hoverInfoDiv = document.getElementById('hoverinfo');
    if (!rows.length) {
      if (plotDiv) {
        await Plotly.react(plotDiv, [], {
          paper_bgcolor: 'transparent',
          plot_bgcolor: 'transparent',
        } as Partial<Plotly.Layout>);
      }
      if (hoverInfoDiv) hoverInfoDiv.innerHTML = '';
      return;
    }

    // --- 5) Axe X selon le tri courant
    const fmt = d3.timeFormat('%d/%m/%Y - %H:%M:%S');
    const isDateSort = this.currentSortKey === 'date';

    // - Tri par date => X = dates (continu)
    // - Autre tri => X = libellés (catégories ordonnées fixes)
    const xData = isDateSort ? rows.map((d) => d.date) : rows.map((d) => fmt(d.date));

    // --- 6) Préparer Y et textes
    const yExposure = rows.map((d) => d.exposure as number);
    const yISO = rows.map((d) => d.iso as number);
    const ySharpness = rows.map((d) => d.sharpness as number);
    const yMovement = rows.map((d) => d.movement as number | undefined);
    const yPerson = rows.map((d) => d.person as number | undefined);

    const textExposure = rows.map((d) => `Exposure: ${d.exposure?.toFixed(1)}<br>${fmt(d.date)}`);
    const textISO = rows.map((d) => `ISO: ${d.iso?.toFixed(1)}<br>${fmt(d.date)}`);
    const textSharpness = rows.map((d) => `Sharpness: ${d.sharpness?.toFixed(1)}<br>${fmt(d.date)}`);
    const textMovement = rows.map((d) =>
      d.movement !== undefined ? `Movement: ${d.movement.toFixed(1)}<br>${fmt(d.date)}` : ''
    );
    const textPerson = rows.map((d) =>
      d.person !== undefined ? `Nombre de personne(s): ${d.person}<br>${fmt(d.date)}` : ''
    );

    // --- 7) Traces communes
    const common: Partial<Plotly.ScatterData> = {
      x: xData as any,
      mode: 'lines+markers',
      type: 'scatter',
      marker: { size: 5 },
      customdata: rows.map((d) => d.imageUrl) as any,
      hovertemplate: '%{text}<extra></extra>',
    };

    const traceExposure: Partial<Plotly.ScatterData> = {
      ...common,
      y: yExposure,
      name: this.$t('timelapseedition.exposure') as string,
      text: textExposure,
    };

    const traceISO: Partial<Plotly.ScatterData> = {
      ...common,
      y: yISO,
      name: this.$t('timelapseedition.iso') as string,
      text: textISO,
    };

    const traceSharpness: Partial<Plotly.ScatterData> = {
      ...common,
      y: ySharpness,
      name: this.$t('timelapseedition.clearness') as string,
      text: textSharpness,
    };

    const traces: Partial<Plotly.ScatterData>[] = [traceExposure, traceISO, traceSharpness];

    if (this.camera.detectionia) {
      traces.push({
        ...common,
        y: yMovement.filter((v) => typeof v === 'number') as number[],
        name: this.$t('timelapseedition.movement') as string,
        text: textMovement,
      });
    }

    traces.push({
      ...common,
      y: yPerson.filter((v) => typeof v === 'number') as number[],
      name: this.$t('timelapseedition.person') as string,
      text: textPerson,
    });

    // --- 8) Layout & config (xaxis selon tri)
    const layout: Partial<Plotly.Layout> = {
      modebar: {
        orientation: 'v',
        bgcolor: '#001a35',
        color: '#6c757d',
        activecolor: '#5fc9e4',
      },
      xaxis: isDateSort
        ? { type: 'date', color: '#5fc9e4', gridcolor: '#6c757d' }
        : {
            type: 'category',
            // fige la position des catégories dans l’ordre EXACT de xData
            categoryorder: 'array',
            categoryarray: xData as any,
            color: '#5fc9e4',
            gridcolor: '#6c757d',
          },
      yaxis: {
        color: '#5fc9e4',
        gridcolor: '#6c757d',
      },
      autosize: true,
      margin: { l: 30, r: 0, b: 31, t: 10, pad: 0 },
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      legend: {
        x: 0.4,
        xanchor: 'left' as const,
        y: 1.04,
        yanchor: 'middle' as const,
        orientation: 'h' as const,
        font: { color: '#5fc9e4' },
      },
    };

    const config = {
      responsive: true,
      scrollZoom: true,
      displaylogo: false,
      displayModeBar: true,
      modeBarButtonsToRemove: ['lasso2d', 'select2d'] as ModeBarDefaultButtons[],
    };

    // --- 9) Render (react au lieu de newPlot pour éviter de recréer)
    await Plotly.react(plotDiv, traces, layout, config);

    // --- 10) Survol : aperçu image
    plotDiv.on('plotly_hover', (ev: any) => {
      const html = ev.points.map(
        (p: any) =>
          `<img src="${p.customdata}" style="max-width:260px;max-height:220px;border-radius:8px;display:block;">`
      );

      const containerEl = this.$refs.container as HTMLElement;
      const rect = containerEl.getBoundingClientRect();

      this.hoverInfoPosition.x = ev.event.clientX - rect.left;
      this.hoverInfoPosition.y = ev.event.clientY - rect.top;

      if (hoverInfoDiv) {
        hoverInfoDiv.innerHTML = html.join('');

        // 1er passage (taille peut être 0 si l'image n'est pas encore chargée)
        requestAnimationFrame(() => {
          // valeurs par défaut : droite + au-dessus
          this.hoverOffsetX = 12;
          this.hoverOffsetY = -(hoverInfoDiv.getBoundingClientRect().height || 200) - 12;
          this.adjustHoverPosition();
        });

        // Re-mesurer après chargement effectif de l'image
        const img = hoverInfoDiv.querySelector('img');
        if (img) {
          img.addEventListener(
            'load',
            () => {
              requestAnimationFrame(() => this.adjustHoverPosition());
            },
            { once: true }
          );
        }
      }
    });

    plotDiv.on('plotly_unhover', () => {
      if (hoverInfoDiv) hoverInfoDiv.innerHTML = '';
    });
  }

  public hoverInfoStyle() {
    return {
      position: 'absolute',
      left: `${this.hoverInfoPosition.x + this.hoverOffsetX}px`,
      top: `${this.hoverInfoPosition.y + this.hoverOffsetY}px`,
      zIndex: 2000,
      // Optionnel: éviter que la souris capture l’overlay
      pointerEvents: 'none',
    };
  }

  private adjustHoverPosition() {
    const containerEl = this.$refs.container as HTMLElement | null;
    const hoverInfoDiv = document.getElementById('hoverinfo');
    if (!containerEl || !hoverInfoDiv) return;

    // Dimensions du conteneur (repère local)
    const cw = containerEl.clientWidth;
    const ch = containerEl.clientHeight;

    // Dimensions réelles du popover
    const hoverRect = hoverInfoDiv.getBoundingClientRect();
    const w = hoverRect.width || 0;
    const h = hoverRect.height || 0;

    const PAD = 20;

    // Par défaut: à droite + au-dessus
    let offX = PAD; // droite
    let offY = -(h + PAD); // au-dessus

    // Position projetée dans le repère du conteneur
    // (hoverInfoPosition est déjà relative au conteneur)
    const projRight = this.hoverInfoPosition.x + offX + w;
    const projLeft = this.hoverInfoPosition.x + offX;
    const projTop = this.hoverInfoPosition.y + offY;
    const projBot = this.hoverInfoPosition.y + offY + h;

    // ---- HORIZONTAL
    if (projRight > cw) {
      // Bascule à gauche du curseur
      offX = -(w + PAD);
      // option: si malgré tout ça sort à gauche, clipper au bord
      if (this.hoverInfoPosition.x + offX < 0) {
        offX = -this.hoverInfoPosition.x + PAD; // collé au bord gauche + PAD
      }
    } else if (projLeft < 0) {
      // Trop à gauche -> remet à droite
      offX = PAD;
    }

    // ---- VERTICAL
    if (projTop < 0) {
      // Trop haut -> bascule dessous
      offY = PAD;
      if (this.hoverInfoPosition.y + offY + h > ch) {
        // Si ça déborde en bas, coller au bas moins PAD
        offY = ch - this.hoverInfoPosition.y - h - PAD;
      }
    } else if (projBot > ch) {
      // Trop bas -> mettre au-dessus
      offY = -(h + PAD);
      if (this.hoverInfoPosition.y + offY < 0) {
        // Si ça déborde encore, coller en haut + PAD
        offY = -this.hoverInfoPosition.y + PAD;
      }
    }

    this.hoverOffsetX = offX;
    this.hoverOffsetY = offY;
  }

  public updateMousePosition(e: any) {
    this.mousePosition.x = e.pageX;
    this.mousePosition.y = e.pageY;
  }

  public updateZoomedPhoto(photoId?: string): void {
    // fige l’ordre courant (visible) pour l’overlay
    this.overlayPhotos = this.visiblePhotos.slice();

    const idx = this.overlayPhotos.findIndex((p) => p.id === photoId);
    if (idx >= 0) {
      this.zoomedPhotoIndex = idx;
    } else {
      // Sécurité : si pas trouvé, on n’ouvre pas
      this.zoomedPhotoIndex = undefined;
    }
  }

  public increasePhotosPerRow(): void {
    if (this.photosPerRow < this.maxPhotosPerRow) {
      this.photosPerRow++;
      this.updatePhotosSize();
    }
  }

  public decreasePhotosPerRow(): void {
    if (this.photosPerRow > this.minPhotosPerRow) {
      this.photosPerRow--;
      this.updatePhotosSize();
    }
  }

  public get captionFontSize(): string {
    const baseFontSize = 14; // taille de base
    const fontSizeRange = 9; // amplitude de variation

    const minC = this.minPhotosPerRow; // dynamique (2 sur mobile, 4 desktop)
    const maxC = this.maxPhotosPerRow;
    const ratio = (this.photosPerRow - minC) / Math.max(1, maxC - minC);
    const fontSize = baseFontSize - fontSizeRange * this.clamp(ratio, 0, 1);

    return `${fontSize}px`;
  }

  public unzoom(): void {
    this.zoomedPhotoIndex = undefined;
    this.overlayPhotos = []; // on libère le snapshot
  }

  private preloadNeighbor(n: number) {
    const list = this.overlayPhotos.length ? this.overlayPhotos : this.visiblePhotos;
    const p = list[n];
    if (!p) return;
    this.resolveS3Url(p.key).then((url) => {
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
    });
  }
  public zoomNext(): void {
    const max = this.overlayActiveList.length;
    if (this.zoomedPhotoIndex !== undefined && this.zoomedPhotoIndex < max - 1) {
      this.zoomedPhotoIndex++;
      this.preloadNeighbor(this.zoomedPhotoIndex + 1); // ← précharge la suivante
    }
  }
  public zoomPrevious(): void {
    if (this.zoomedPhotoIndex !== undefined && this.zoomedPhotoIndex > 0) {
      this.zoomedPhotoIndex--;
      this.preloadNeighbor(this.zoomedPhotoIndex - 1); // ← précharge la précédente
    }
  }

  private rowsCount(p: Photo): 1 | 2 {
    // même logique que orderedMeta(), mais on compte les *vraies* valeurs
    const visible = this.buildMeta(p).length; // buildMeta ne renvoie que les réels
    return visible <= 3 ? 1 : 2;
  }

  private resizeObserver: ResizeObserver | null = null;
  private frameId: number | null = null;

  public initResizeObserver(): void {
    if (!this.resizeObserver && this.photos) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.frameId !== null) {
          cancelAnimationFrame(this.frameId);
        }
        this.frameId = requestAnimationFrame(this.updatePhotosSize);
      });
      this.resizeObserver.observe(this.photos);
      document.addEventListener('mousemove', this.updateMousePosition);
    }
  }

  private isMobileViewport(): boolean {
    // ajuste le breakpoint si besoin (ex: 768px, 820px, etc.)
    return window.matchMedia('(max-width: 1000px)').matches;
  }

  private clamp(n: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, n));
  }

  /**
   * Applique les bornes/valeurs selon le viewport.
   * - Mobile: min=2, max=4, défaut=3
   * - Desktop: min=4, max=20, défaut=8 (comportement actuel)
   * Préserve la valeur actuelle si possible, sinon recadre.
   */
  private applyResponsiveColumns(forceDefault = false): void {
    const isMobile = this.isMobileViewport();

    const min = isMobile ? this.MOBILE_MIN : this.DESKTOP_MIN;
    const max = isMobile ? this.MOBILE_MAX : this.DESKTOP_MAX;
    const def = isMobile ? this.MOBILE_DEFAULT : this.DESKTOP_DEFAULT;

    this.minPhotosPerRow = min;
    this.maxPhotosPerRow = max;

    if (forceDefault) {
      this.photosPerRow = def;
    } else {
      // on conserve dans la mesure du possible, sinon on recadre proprement
      this.photosPerRow = this.clamp(this.photosPerRow, min, max);
    }

    // recalcul immédiat des tailles
    this.updatePhotosSize();
  }

  private handleWindowResize = () => {
    // si on bascule de breakpoint, on recadre les bornes + tailles
    this.applyResponsiveColumns(false);
  };

  public mounted(): void {
    logger.info('mounted');
    this.localImages = [...this.dataImagesKey];
    this.localModel = { ...this.value };

    // 1) Appliquer d’emblée les bons min/max et la valeur par défaut selon viewport
    this.applyResponsiveColumns(true);

    this.applyCurrentSort();

    if (!this.loadingPhoto && !this.loadingTimelapse) {
      this.onDataReady();
    }
    this.initResizeObserver();

    // 2) Suivre les changements de viewport (rotation, split view, etc.)
    window.addEventListener('resize', this.handleWindowResize, { passive: true });

    const container = this.$refs.container as HTMLElement;
    if (container) {
      container.addEventListener('wheel', this.handleWheelZoom, { passive: false });
    }

    this.$nextTick(() => {
      this.updatePhotosSize();
      console.log('scrollerWidth:', this.scrollerWidth, 'scrollerHeight:', this.scrollerHeight);
      // AJOUT ICI : Premier emit pour sync initial
      this.$emit('update-photo-count', this.enabledCount);
    });
  }

  public updated(): void {
    if (this.dataReady) {
      this.initResizeObserver();
    }
  }

  public beforeDestroy(): void {
    window.removeEventListener('keydown', this.onOvKey);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.frameId !== null) {
      cancelAnimationFrame(this.frameId);
    }
    window.removeEventListener('resize', this.handleWindowResize);

    const container = this.$refs.container as HTMLElement;
    if (container) {
      container.removeEventListener('wheel', this.handleWheelZoom);
    }
  }

  public getRotation(): string {
    return this.camera && this.camera.rotate ? `rotate(${this.camera.rotate}deg)` : '';
  }

  public updatePhotosSize(): void {
    const w = this.photos.clientWidth;
    this.scrollerHeight = Math.max(0, this.photos.clientHeight - 22);
    this.scrollerWidth = w;
    this.scrollerImageWidth = (w - 15) / this.photosPerRow;
    this.$emit('update-photo-size');
    this.rerender++;
  }

  public cellSizeAndPositionGetter(
    item: { data: Photo },
    index: number
  ): { width: number; height: number; x: number; y: number } {
    const w = this.scrollerImageWidth;
    const h = this.scrollerImageWidth * (112 / 150); // Ajustez selon le ratio souhaité
    return {
      width: w,
      height: h,
      x: (index % this.photosPerRow) * w,
      y: Math.floor(index / this.photosPerRow) * h,
    };
  }

  private formatNumber(v?: number, digits = 1) {
    return typeof v === 'number' ? v.toFixed(digits) : '';
  }

  private orderedMeta(p: Photo) {
    // récupère ce que tu construis déjà
    const map = new Map(this.buildMeta(p).map((it) => [it.key, it]));

    // ordre *fixe* (3 en haut, 3 en bas)
    const order = ['exp', 'iso', 'sharp', 'move', 'person', 'goutte'];

    // icônes par défaut (sert aussi aux placeholders)
    const defaultIcons: Record<string, string> = {
      exp: 'fa-regular fa-sun',
      iso: this.camera?.isTikee !== undefined ? 'fa-solid fa-gauge' : 'fa-solid fa-signal',
      sharp: 'fa-regular fa-gem',
      move: 'fa-solid fa-wave-square',
      person: 'fa-solid fa-user',
      goutte: 'fa-solid fa-droplet',
    };

    // titres par défaut (au cas où)
    const titles: Record<string, string> = {
      exp: this.$t('timelapseedition.exposure') as string,
      iso: this.$t('timelapseedition.iso') as string,
      sharp: this.$t('timelapseedition.clearness') as string,
      move: this.$t('timelapseedition.movement') as string,
      person: this.$t('timelapseedition.person') as string,
      goutte: this.$t('timelapseedition.goutte') as string,
    };

    // fabrique 6 items exactement, en gardant la place pour les absents
    return order.map((key) => {
      const found = map.get(key);
      if (found) return { ...found, placeholder: false };
      return {
        key,
        icon: defaultIcons[key],
        display: '', // rien à afficher
        title: titles[key],
        placeholder: true, // réserve l’emplacement
      };
    });
  }

  public buildMeta(p: Photo) {
    // Détermine si la caméra expose ISO ou Gain
    const isISO = this.camera?.isTikee !== undefined;
    const isoOrGainLabel = isISO ? 'ISO' : 'Gain';
    const isoOrGainUnit = isISO ? '' : ' dB';

    const items: { key: string; icon: string; display: string; title: string }[] = [];

    if (typeof p.exposure === 'number') {
      // Expo en ms, horloge = plus parlant qu'une caméra pour le temps
      items.push({
        key: 'exp',
        icon: 'fa-regular fa-sun',
        display: `${this.formatNumber(p.exposure, 1)}`,
        title: this.$t('timelapseedition.exposure') as string,
      });
    }

    if (typeof p.gainControl === 'number') {
      items.push({
        key: 'iso',
        icon: isISO ? 'fa-solid fa-gauge' : 'fa-solid fa-signal',
        display: `${Math.round(p.gainControl)}${isoOrGainUnit}`,
        title: isoOrGainLabel,
      });
    }

    if (typeof p.sharpness === 'number') {
      items.push({
        key: 'sharp',
        icon: 'fa-regular fa-gem',
        display: this.formatNumber(p.sharpness, 1),
        title: this.$t('timelapseedition.clearness') as string,
      });
    }

    if (typeof p.person === 'number' && p.person > 0) {
      items.push({
        key: 'person',
        icon: 'fa-solid fa-user',
        display: `${p.person}`,
        title: this.$t('timelapseedition.person') as string,
      });
    }

    if (p.detectedLabels && typeof p.detectedLabels.movement === 'number') {
      items.push({
        key: 'move',
        icon: 'fa-solid fa-wave-square',
        display: this.formatNumber(p.detectedLabels.movement, 1),
        title: this.$t('timelapseedition.movement') as string,
      });
    }

    if (p.detectedLabels && typeof p.detectedLabels.goutte === 'number') {
      items.push({
        key: 'goutte',
        icon: 'fa-solid fa-droplet',
        display: this.formatNumber(p.detectedLabels.goutte, 1),
        title: this.$t('timelapseedition.goutte') as string,
      });
    }

    return items;
  }

  public formatDate(date: string | Date): string {
    if (!date) return '';
    const d = new Date(date);
    const locale = this.locale === 'en' ? 'en-GB' : 'fr-FR';

    // On formate séparément la DATE et l’HEURE
    const dateStr = new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(d);

    const timeStr = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);

    // Résultat garanti : "30/09/2025 - 10:15"
    return `${dateStr} - ${timeStr}`;
  }

  private onOvKey = (e: KeyboardEvent) => {
    if (this.zoomedPhotoIndex === undefined) return;

    // Empêche le scroll avec les flèches
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === '+' || e.key === '-') {
      e.preventDefault();
    }

    switch (e.key) {
      case 'Escape':
        this.unzoom();
        break;
      case 'ArrowLeft':
        this.zoomPrevious();
        break;
      case 'ArrowRight':
        this.zoomNext();
        break;
      case '+':
        (this.$refs.zoomerFullScreen as any)?.zoomIn?.();
        break;
      case '-':
        (this.$refs.zoomerFullScreen as any)?.zoomOut?.();
        break;
    }
  };

  private async resolveS3Url(key: string): Promise<string> {
    const res = await Storage.get(key);
    return typeof res === 'string' ? res : String(res);
  }

  // Précharge et décode l’image avant d’afficher (évite le flash sombre)
  private preload(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      let done = false;
      const img = new Image();
      img.decoding = 'async';
      (img as any).fetchPriority = 'high'; // hint (support partiel)

      const finish = () => {
        if (!done) {
          done = true;
          resolve();
        }
      };
      img.onload = finish;
      img.onerror = () => {
        if (!done) {
          done = true;
          resolve();
        }
      }; // on résout quand même
      img.src = src;

      // decode() si dispo -> garantit le rendu sans “top-to-bottom”
      if (typeof (img as any).decode === 'function') {
        (img as any).decode().then(finish).catch(finish);
      }
    });
  }

  // Affiche une photo (avec crossfade si une image est déjà visible)
  private async showPhoto(photo: Photo): Promise<void> {
    if (!photo) return;
    const seq = ++this.imgLoadSeq;

    const url = await this.resolveS3Url(photo.key); // clé full-res
    await this.preload(url);
    if (seq !== this.imgLoadSeq) return;

    if (!this.activeImageUrl) {
      this.activeImageUrl = url;
      this.nextImageUrl = null;
      this.isCrossfading = false;
      return;
    }

    this.nextImageUrl = url;
    await this.$nextTick();
    requestAnimationFrame(() => {
      this.isCrossfading = true;
      window.setTimeout(() => {
        if (seq !== this.imgLoadSeq) return;
        this.activeImageUrl = url;
        this.nextImageUrl = null;
        this.isCrossfading = false;
      }, this.FADE_MS);
    });
  }

  @Watch('localModel', { deep: true })
  @Watch('localImages', { deep: true })
  private onEnabledCountChange() {
    const count = this.enabledCount;
    console.log('🔍 TimelapsePhoto - enabledCount changed to:', count); // LOG DEBUG
    this.$emit('update-photo-count', count); // ÉMET L'EVENT VERS PARENT
  }

  @Watch('zoomedPhoto', { immediate: true })
  onZoomedPhotoForFade(val: Photo | null) {
    if (val) {
      this.showPhoto(val);
    } else {
      this.activeImageUrl = null;
      this.nextImageUrl = null;
      this.isCrossfading = false;
    }
  }

  @Watch('zoomedPhotoIndex')
  onZoomedIndexChange(val?: number) {
    if (val !== undefined) {
      this.$nextTick(() => (this.$refs.overlay as HTMLElement | undefined)?.focus?.());
    }
  }

  @Watch('zoomedPhoto')
  onZoomedPhotoChange(val: Photo | null) {
    if (val) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', this.onOvKey);
    } else {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', this.onOvKey);
    }
  }

  @Watch('dataImagesKey')
  public onPhotosChange(newVal: { data: Photo }[], oldVal: { data: Photo }[]): void {
    if (this.graphActivated) {
      this.createChartDebounced();
    }
  }

  @Watch('dataImagesKey', { immediate: true })
  onDataImagesKeyChange(newVal: { data: Photo }[]) {
    const same =
      this.localImages.length === newVal.length && this.localImages.every((w, i) => w.data.id === newVal[i].data.id);

    if (!same) {
      this.localImages = newVal; // mêmes références → cheap
      // ne touche pas à rerender ici
      this.applyCurrentSort();
      if (this.graphActivated) this.$nextTick(this.createChart);
    }
  }

  @Watch('loadingTimelapse')
  public onLoadingTimelapse(val: boolean, oldVal: boolean): void {
    logger.info('loadingTimelapse');
    if (oldVal && !val && !this.loadingPhoto) {
      this.onDataReady();
    }
  }
  @Watch('loadingPhoto')
  public onLoadingPhoto(val: boolean, oldVal: boolean): void {
    logger.info('loadingPhoto');
    if (oldVal && !val && !this.loadingTimelapse) {
      this.onDataReady();
    }
  }

  public onDataReady(): void {
    logger.info('onDataReady', { model: this.model });
    this.dataReady = true;
    this.$nextTick().then(() => {
      this.updatePhotosSize();
      console.log('scrollerWidth:', this.scrollerWidth, 'scrollerHeight:', this.scrollerHeight);
    });
  }

  public toggleSelectAll(): void {
    const list = this.selectablePhotos; // seulement celles non "removed"
    const allSelected = list.length > 0 && list.every((p) => this.selectedPhoto.some((sp) => sp.id === p.id));

    this.selectedPhoto = allSelected ? [] : list.slice(); // garder les mêmes références
    this.$nextTick(() => {
      const tt = this.$refs.shareTooltip as any;
      if (tt) tt.$emit('close');
    });
  }

  public togglePhotoInSelection(photo: Photo): void {
    if (this.selectedPhoto.indexOf(photo) === -1) {
      this.selectedPhoto.push(photo);
    } else {
      this.selectedPhoto = this.selectedPhoto.filter((p) => p.id !== photo.id);
    }
    this.lastSelectedPhoto = photo;
  }

  get allVisibleSelected(): boolean {
    const list = this.visiblePhotos; // inclut les remove
    return list.length > 0 && list.every((p) => this.selectedPhoto.some((sp) => sp.id === p.id));
  }

  /** Sélectionne / désélectionne toutes les photos visibles (y compris remove) */
  public toggleSelectAllVisible(): void {
    const list = this.visiblePhotos; // inclut les remove
    const allSelected = list.length > 0 && list.every((p) => this.selectedPhoto.some((sp) => sp.id === p.id));
    this.selectedPhoto = allSelected ? [] : list.slice(); // conserve les mêmes références d'objets
  }

  public selectClick(photo: Photo): void {
    const list = this.visiblePhotos; // <-- au lieu de dataImagesKey.map(...)
    if (this.lastSelectedPhoto === undefined) {
      this.togglePhotoInSelection(photo);
    } else {
      const firstIndex = list.indexOf(this.lastSelectedPhoto);
      const lastIndex = list.indexOf(photo);
      const [a, b] = firstIndex <= lastIndex ? [firstIndex, lastIndex] : [lastIndex, firstIndex];
      for (let i = a; i <= b; i++) this.togglePhotoInSelection(list[i]);
    }
  }

  public downloadZip(): void {
    this.showModalZip = true;
    this.zipLink = null;
    this.zipGenerating = true;
    this.connectSocket();
    photoService.requestZipPhoto(
      this.project.id,
      this.selectedPhoto.map((p) => p.id)
    );
  }

  @Watch('socketMessage.message')
  public onSocketMessageChange(val: NotificationMessage, oldVal: NotificationMessage): void {
    if (val && val.zip && val.zip === 'ready') {
      logger.info('onSocketMessageChange', { val, oldVal });
      this.zipGenerating = false;
      this.zipLink = (val.url as string).split('|');
      logger.info({ zipLink: this.zipLink });
    }
  }

  public disablePhoto(): void {
    if (this.selectedPhoto.length > 0) {
      this.selectedPhoto.forEach((photo) => {
        if (this.model.photoExclusion.indexOf(photo.id) === -1) {
          this.model.photoExclusion.push(photo.id);
        } else {
          this.model.photoExclusion = this.model.photoExclusion.filter((id) => id !== photo.id);
        }
      });
      this.selectedPhoto = [];
      // @ts-ignore vue bootstrap component
      this.$refs.removeTooltip.$emit('close');
    }
  }

  public async deletePhoto(): Promise<void> {
    if (this.selectedPhoto.length > 0) {
      const titleMessage = this.$t('global.confirm').toString();
      const deleteMessage = this.$t('global.delete').toString();
      const cancelMessage = this.$t('global.close').toString();
      this.$bvModal
        .msgBoxConfirm(this.$t('timelapsefilter.validatedeleteall').toString(), {
          title: titleMessage,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: deleteMessage,
          cancelTitle: cancelMessage,
          headerClass: 'text-danger',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        })
        .then((value) => {
          if (value) {
            this.deletePhotos({
              projectId: this.project.id,
              photoIds: this.selectedPhoto.map((p) => p.id),
              cameraId: this.camera.id,
            });
            this.selectedPhoto = [];
            // @ts-ignore vue boostrap component
            this.$refs.deleteTooltip.$emit('close');
          }
        });
    }
  }

  public save(): void {
    this.$emit('input', this.localModel);
    this.$emit('save');
  }
}
